import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Car, Search, CheckCircle, AlertTriangle, Package, PackageCheck,
  Clock, ChevronRight, Gauge
} from "lucide-react";
import { toast } from "sonner";

export default function CollaborateurDossiersView() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const isMecanicien = user?.specialite === "mecanicien";

  // Titre selon la spécialité
  const getTitle = () => {
    const titles = {
      carrossier: "Dossiers Carrosserie",
      peintre: "Dossiers Peinture",
      mecanicien: "Dossiers Mécanique",
      preparateur_peintre: "Dossiers Préparation Peinture"
    };
    return titles[user?.specialite] || "Mes Dossiers";
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const [vehiclesRes, stepsRes] = await Promise.all([
        api.get("/vehicles"),
        api.get("/workflow-steps")
      ]);
      
      setWorkflowSteps(stepsRes.data || []);
      
      // Obtenir les étapes pour la spécialité de l'utilisateur
      const userSteps = (stepsRes.data || [])
        .filter(s => s.specialite === user?.specialite)
        .map(s => s.name);
      
      // Pour les mécaniciens, inclure aussi les véhicules en type_intervention mécanique
      // et ceux à l'étape "entretien_mecanique" ou "none" avec type mécanique
      const filtered = vehiclesRes.data.filter(v => {
        // Exclure TOUS les terminés (par statut ou par étape workflow)
        if (v.statut === "termine" || v.workflow_step === "termine" || v.workflow_step === "livraison_facturation") {
          return false;
        }
        
        // Pour les mécaniciens - UNIQUEMENT mécanique et NON terminé
        if (isMecanicien) {
          // Le véhicule doit être de type mécanique
          const isMecaVehicle = v.type_intervention === "mecanique" || 
                 v.workflow_step === "entretien_mecanique" ||
                 (v.workflow_step === "none" && v.type_intervention === "mecanique");
          return isMecaVehicle;
        }
        
        // Pour les carrossiers/peintres - EXCLURE mécanique
        if (v.type_intervention === "mecanique") {
          return false;
        }
        
        // Pour les autres spécialités, vérifier l'étape workflow
        return userSteps.includes(v.workflow_step);
      });
      
      // Filtrer par date: uniquement RDV passés non terminés + semaine en cours
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dayOfWeek = today.getDay(); // 0=dimanche
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() + mondayOffset);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);
      
      const dateFiltered = filtered.filter(v => {
        if (!v.date_rdv) return true; // pas de date = on affiche
        const rdvDate = new Date(v.date_rdv + "T00:00:00");
        // RDV passé (en retard) ou RDV de la semaine en cours
        return rdvDate < today || (rdvDate >= weekStart && rdvDate <= weekEnd);
      });
      
      setVehicles(dateFiltered);
    } catch (error) {
      console.error("Erreur chargement dossiers:", error);
    }
    setLoading(false);
  };

  // Filtrer par recherche
  const filteredVehicles = vehicles.filter(v => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      v.immatriculation?.toLowerCase().includes(searchLower) ||
      v.marque?.toLowerCase().includes(searchLower) ||
      v.modele?.toLowerCase().includes(searchLower) ||
      v.client?.nom?.toLowerCase().includes(searchLower)
    );
  });

  // Obtenir le label de l'étape workflow
  const getStepLabel = (stepId) => {
    if (!stepId || stepId === "none") return "À planifier";
    const step = workflowSteps.find(s => s.name === stepId);
    return step?.label || stepId;
  };

  // Couleur selon l'étape
  const getStepColor = (step) => {
    const colors = {
      expertise_geometrie: "bg-blue-100 text-blue-700",
      redressage: "bg-orange-100 text-orange-700",
      preparation_peinture: "bg-yellow-100 text-yellow-700",
      peinture: "bg-purple-100 text-purple-700",
      remontage: "bg-emerald-100 text-emerald-700",
      lavage: "bg-cyan-100 text-cyan-700",
      controle_qualite: "bg-pink-100 text-pink-700",
      entretien_mecanique: "bg-slate-100 text-slate-700"
    };
    return colors[step] || "bg-slate-100 text-slate-700";
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">{getTitle()}</h1>
          <p className="text-slate-500">{filteredVehicles.length} dossier(s) en cours</p>
        </div>
        
        {/* Recherche */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher (immat, marque, client...)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Car className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{vehicles.length}</p>
              <p className="text-xs text-slate-500">Total</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Package className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {vehicles.filter(v => !v.pieces_commandees).length}
              </p>
              <p className="text-xs text-slate-500">Pièces manquantes</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {vehicles.filter(v => v.is_blocked).length}
              </p>
              <p className="text-xs text-slate-500">Bloqués</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Clock className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">
                {vehicles.filter(v => v.priorite).length}
              </p>
              <p className="text-xs text-slate-500">Prioritaires</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des dossiers */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Tous les dossiers</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner" />
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
              <p className="font-semibold">Aucun dossier en cours</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredVehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all hover:border-red-300 hover:shadow-sm ${
                    vehicle.is_blocked 
                      ? "border-red-300 bg-red-50" 
                      : vehicle.priorite
                      ? "border-red-200 bg-red-50/50"
                      : "border-slate-200 bg-white"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {/* Indicateur priorité */}
                      {vehicle.priorite && (
                        <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" title="Prioritaire" />
                      )}
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-slate-900">
                            {vehicle.immatriculation}
                          </span>
                          {vehicle.pieces_commandees ? (
                            <PackageCheck className="h-4 w-4 text-emerald-500" title="Pièces OK" />
                          ) : (
                            <Package className="h-4 w-4 text-red-500" title="Pièces manquantes" />
                          )}
                          {vehicle.is_blocked && (
                            <AlertTriangle className="h-4 w-4 text-red-500" title="Bloqué" />
                          )}
                          {vehicle.kilometrage && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              <Gauge className="h-3 w-3" />
                              {vehicle.kilometrage.toLocaleString()} km
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600">
                          {vehicle.marque} {vehicle.modele}
                        </p>
                        {vehicle.client?.nom && (
                          <p className="text-xs text-slate-500">
                            {vehicle.client.nom} {vehicle.client.prenom}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Badge className={getStepColor(vehicle.workflow_step)}>
                        {getStepLabel(vehicle.workflow_step)}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                  
                  {vehicle.description && (
                    <p className="mt-2 text-xs text-slate-500 italic truncate pl-6">
                      {vehicle.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
